import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Save, Check } from 'lucide-react';
import { governanceMeetingsService, GovernanceMeeting } from '../../../services/governanceService';
import {
    Task as ApiTask, tasksService,
} from '../../../services/tasksService';
import { capaInvestigationsService, CapaInvestigation } from '../../../services/capaInvestigationsService';
import Modal from '../../../components/common/Modal';
import Button from '../../../components/common/Button';
import TaskForm from '../../../components/tasks/TaskForm';
import TaskActionPanel from '../../../components/tasks/TaskActionPanel';
import InvestigationView from '../../capa/InvestigationView';
import MeetingDetailModal from '../../../components/governance/MeetingDetailModal';
import WorkTabs, { WorkTabKey, WorkScope } from './WorkTabs';
import WorkNetBody from './WorkNetBody';

type MyMeeting = GovernanceMeeting & { my_role: 'Chairman' | 'Participant'; chairman_name?: string };

// Mock Data Interfaces (legacy — kept for AUDIT/COMPLIANCE/CAPA mock feed)
interface Task {
    id: number;
    type: string;
    title: string;
    dueDate: string;
    status: string;
    priority: string;
    context: string;
    description: string;
}

interface Scope {
    sops: {
        id: number;
        name: string;
        role: string;
        description: string;
        frequency: string;
        lastUpdated?: string;
    }[];
    risks: {
        id: number;
        name: string;
        level: string;
        role: string;
        mitigation: string;
    }[];
    compliances: {
        id: number;
        name: string;
        role: string;
        frequency: string;
        authority: string;
    }[];
}

interface WorkViewProps {
    tasks: Task[];
    rawTasks?: ApiTask[];
    inboxLoading?: boolean;
    scope: Scope;
    // 'active' = Action Centre (default). 'archive' = History: closed records, read-only.
    workScope?: WorkScope;
}

const ALL_TABS: WorkTabKey[] = [
    'audit', 'compliance', 'statutory', 'control-testing', 'it-assessment', 'risk-assessments',
    'process-walkthroughs-design', 'process-walkthroughs-compliance', 'renewals', 'reviews', 'capa', 'tasks', 'meetings', 'projects',
];

// First tab shown when no (valid) ?tab= is present. Inbox moved to the
// Plan/Calendar surface; My Tasks now leads the rail and is the landing tab.
const DEFAULT_TAB: WorkTabKey = 'tasks';

const WorkView: React.FC<WorkViewProps> = ({ tasks, rawTasks = [], inboxLoading = false, scope, workScope = 'active' }) => {
    const readOnly = workScope === 'archive';
    // Tab + deep-link sync via URL. Calendar drill-down navigates to
    // /work?tab=<tabKey>&open=<recordId> — we read both here, switch tab,
    // then auto-open the matching record's modal once data is loaded.
    const [searchParams, setSearchParams] = useSearchParams();
    const urlTab = searchParams.get('tab') as WorkTabKey | null;
    const urlOpen = searchParams.get('open');
    const initialTab: WorkTabKey =
        urlTab && ALL_TABS.includes(urlTab) ? urlTab : DEFAULT_TAB;

    const [activeTab, setActiveTabState] = useState<WorkTabKey>(initialTab);

    const setActiveTab = useCallback((next: WorkTabKey) => {
        setActiveTabState(next);
        // Keep tab in URL for refresh/bookmark; clear any stale `open`.
        setSearchParams((sp) => {
            const newSp = new URLSearchParams(sp);
            newSp.set('tab', next);
            newSp.delete('open');
            return newSp;
        }, { replace: true });
    }, [setSearchParams]);

    // React to URL tab changes (back/forward).
    useEffect(() => {
        if (urlTab && ALL_TABS.includes(urlTab) && urlTab !== activeTab) {
            setActiveTabState(urlTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlTab]);

    const clearOpenParam = useCallback(() => {
        setSearchParams((sp) => {
            const newSp = new URLSearchParams(sp);
            newSp.delete('open');
            return newSp;
        }, { replace: true });
    }, [setSearchParams]);

    const [meetings, setMeetings] = useState<MyMeeting[]>([]);
    const [meetingsLoading, setMeetingsLoading] = useState(false);
    const [meetingsError, setMeetingsError] = useState<string | null>(null);
    const [selectedMeeting, setSelectedMeeting] = useState<MyMeeting | null>(null);

    // Real /api/v1/cap/my-investigations state — wires the existing CAPA tab (D4)
    const [capaInvestigations, setCapaInvestigations] = useState<CapaInvestigation[]>([]);
    const [capaLoading, setCapaLoading] = useState(false);
    const [capaError, setCapaError] = useState<string | null>(null);
    const [openInvestigationId, setOpenInvestigationId] = useState<number | null>(null);

    // Real /api/v1/tasks state — the "Tasks" tab of the Action Center.
    const [myTasks, setMyTasks] = useState<ApiTask[]>([]);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [tasksError, setTasksError] = useState<string | null>(null);
    const [selectedTask, setSelectedTask] = useState<ApiTask | null>(null);
    const [editTask, setEditTask] = useState<ApiTask | null>(null);
    const [showCreate, setShowCreate] = useState(false);
    const handleSelectLegacyTask = (task: Task) => {
        const raw = rawTasks.find(t => t.id === task.id);
        if (raw) setSelectedTask(raw);
    };

    // A task is "completed for this user" when it has reached a terminal state.
    // VERIFIED = "Pending Ratification" — all roles (doer, approver, validator)
    // see it in the active kanban so everyone can track it until it's DONE.
    const isCompletedForUser = (t: ApiTask): boolean => {
        if (['DONE', 'ARCHIVED', 'CANCELLED', 'REJECTED'].includes(t.status)) return true;
        return false;
    };
    const activeTasks = myTasks.filter(t => !isCompletedForUser(t));
    const completedTasks = myTasks.filter(isCompletedForUser);

    const reloadMeetings = async () => {
        setMeetingsLoading(true);
        setMeetingsError(null);
        try {
            const data = await governanceMeetingsService.getMine(false);
            setMeetings(data);
        } catch (e: any) {
            setMeetingsError(e?.message || 'Failed to load meetings');
        } finally {
            setMeetingsLoading(false);
        }
    };

    const reloadTasks = async () => {
        setTasksLoading(true);
        setTasksError(null);
        try {
            const data = await tasksService.list({ mine: true, limit: 75 });
            setMyTasks(data);
        } catch (e: any) {
            setTasksError(e?.message || 'Failed to load tasks');
        } finally {
            setTasksLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab !== 'meetings') return;
        let cancelled = false;
        (async () => {
            setMeetingsLoading(true);
            setMeetingsError(null);
            try {
                const data = await governanceMeetingsService.getMine(false);
                if (!cancelled) setMeetings(data);
            } catch (e: any) {
                if (!cancelled) setMeetingsError(e?.message || 'Failed to load meetings');
            } finally {
                if (!cancelled) setMeetingsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== 'tasks') return;
        let cancelled = false;
        (async () => {
            setTasksLoading(true);
            setTasksError(null);
            try {
                const data = await tasksService.list({ mine: true, limit: 75 });
                if (!cancelled) setMyTasks(data);
            } catch (e: any) {
                if (!cancelled) setTasksError(e?.message || 'Failed to load tasks');
            } finally {
                if (!cancelled) setTasksLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== 'capa') return;
        let cancelled = false;
        (async () => {
            setCapaLoading(true);
            setCapaError(null);
            try {
                // History shows my CLOSED investigations; Action Centre shows open ones.
                const data = readOnly
                    ? await capaInvestigationsService.list({ mine: true, inv_status: 'CLOSED' })
                    : await capaInvestigationsService.myInvestigations();
                if (!cancelled) setCapaInvestigations(data);
            } catch (e: any) {
                if (!cancelled) setCapaError(e?.response?.data?.detail || e?.message || 'Failed to load CAPA');
            } finally {
                if (!cancelled) setCapaLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, readOnly]);

    // Auto-open meeting / task from ?open=<id> once the relevant list is loaded.
    useEffect(() => {
        if (!urlOpen || activeTab !== 'meetings' || meetings.length === 0) return;
        const id = parseInt(urlOpen, 10);
        if (!Number.isFinite(id)) return;
        const m = meetings.find((x) => x.id === id);
        if (m) {
            setSelectedMeeting(m);
            clearOpenParam();
        }
    }, [urlOpen, activeTab, meetings, clearOpenParam]);

    useEffect(() => {
        if (!urlOpen || activeTab !== 'tasks' || myTasks.length === 0) return;
        const id = parseInt(urlOpen, 10);
        if (!Number.isFinite(id)) return;
        const t = myTasks.find((x) => x.id === id);
        if (t) {
            setSelectedTask(t);
            clearOpenParam();
        }
    }, [urlOpen, activeTab, myTasks, clearOpenParam]);

    const handleTaskMutated = (updated: ApiTask) => {
        setMyTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
        // Refresh the list shortly after — the user's role membership may have
        // shifted (e.g., revert routed it to a different person).
        reloadTasks();
    };

    return (
        <div className="module-page">
            <div className={`work-shell${readOnly ? ' work-shell--archive' : ''}`}>
                <WorkTabs
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    scope={workScope}
                />
                <div className="work-shell-main">
                    <WorkNetBody
                        activeTab={activeTab}
                        scope={workScope}
                        readOnly={readOnly}
                        tasks={tasks}
                        inboxLoading={inboxLoading}
                        apiTasks={myTasks}
                        activeTasks={activeTasks}
                        completedTasks={completedTasks}
                        tasksLoading={tasksLoading}
                        tasksError={tasksError}
                        capaInvestigations={capaInvestigations}
                        capaLoading={capaLoading}
                        capaError={capaError}
                        meetings={meetings}
                        meetingsLoading={meetingsLoading}
                        meetingsError={meetingsError}
                        asideOpen={false}
                        onAsideToggle={() => {}}
                        onSelectTask={handleSelectLegacyTask}
                        onSelectApiTask={setSelectedTask}
                        onSelectMeeting={(m) => setSelectedMeeting(m as MyMeeting)}
                        onSelectCapaId={setOpenInvestigationId}
                        onCreateTask={() => setShowCreate(true)}
                    />
                </div>
            </div>

            {/* Create Task modal */}
            <Modal data-testid="MOD_work_create_task" isOpen={showCreate} onClose={() => setShowCreate(false)} size="lg">
                <Modal.Header>Create Task</Modal.Header>
                <Modal.Body>
                    <TaskForm
                        formId="staff-create-task-form"
                        onSaved={() => { setShowCreate(false); reloadTasks(); }}
                        onCancel={() => setShowCreate(false)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button data-testid="BTN_work_create_task_submit" variant="fill" icon={Save} type="submit" form="staff-create-task-form">Submit</Button>
                </Modal.Footer>
            </Modal>

            {/* Action panel */}
            {selectedTask && (
                <TaskActionPanel
                    task={selectedTask}
                    isOpen={!!selectedTask}
                    readOnly={readOnly}
                    onClose={() => setSelectedTask(null)}
                    onMutated={handleTaskMutated}
                    onEditPlan={(t) => { setSelectedTask(null); setEditTask(t); }}
                />
            )}

            {/* Edit Plan modal — opened from the action panel when the user
                is the creator and the task is currently reverted to them.   */}
            {editTask && (
                <Modal data-testid="MOD_work_edit_task" isOpen={!!editTask} onClose={() => setEditTask(null)} size="lg">
                    <Modal.Header>Edit Plan — {editTask.name}</Modal.Header>
                    <Modal.Body>
                        <TaskForm
                            initial={editTask}
                            formId="staff-edit-task-form"
                            onSaved={(updated) => {
                                handleTaskMutated(updated);
                                setEditTask(null);
                                // Re-open the action panel on the freshly-edited task so
                                // the user can immediately Acknowledge & Resume.
                                setSelectedTask(updated);
                            }}
                            onCancel={() => setEditTask(null)}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button data-testid="BTN_work_edit_task_update" variant="update" icon={Check} type="submit" form="staff-edit-task-form">Update</Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* T10: Meeting detail popup — same MeetingDetailModal as the
                GRC admin surface, opened in participant viewMode so the
                modal force-suppresses chair-only edit affordances per
                CONTEXT D5 / SPEC R15-R16. Voting + tab navigation remain
                available because the canEdit gate is independent. */}
            {selectedMeeting && (
                <MeetingDetailModal
                    meeting={selectedMeeting}
                    viewMode="participant"
                    readOnly={readOnly}
                    onClose={() => setSelectedMeeting(null)}
                    onMeetingUpdated={() => reloadMeetings()}
                />
            )}

            {/* CAPA Investigation popup — same InvestigationView component as the
                route, just rendered in a modal so staff stay in the WorkView. */}
            {openInvestigationId !== null && (
                <Modal isOpen={true} onClose={() => setOpenInvestigationId(null)} size="xl">
                    <InvestigationView
                        investigationIdProp={openInvestigationId}
                        readOnly={readOnly}
                        onClose={() => setOpenInvestigationId(null)}
                    />
                </Modal>
            )}
        </div>
    );
};

export default WorkView;
